import { useAppContext } from '@app/react-common/AppContext';
import CountriesAndRegionsDropdown from '@app/react-common/components/countries-and-regions-dropdown/CountriesAndRegionsDropdown';
import { ValueDropdownOptions } from '@app/react-common/components/rule-engine-v2/mapping/ValueDropdownOptions';
import { CurrencyCode } from '@pdqdev/pdq-shared/dist/entities';
import { currencyUnitsToCurrencySymbolMap } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import React, { useEffect } from 'react';
import { Autocomplete } from '../../autocomplete/Autocomplete';
import Dropdown, { Option } from '../../dropdown/Dropdown';
import Input from '../../input/Input';
import { StepperInput } from '../../stepper-input/StepperInput';
import { PROPERTY_OPERATOR_MAPPING, PropertyOperatorMapping, TimeFrame } from '../mapping/PropertyOperatorMapping';
import { Rule } from '../models/Rule';
import {
  Operator,
  Property,
  RuleEngineBindings,
  RuleEngineConfig,
  RuleEngineConfigPropertyTypes,
} from '../models/RuleEngineConfig';
import { RuleEngineState } from '../models/RuleEngineState';
import { validateRuleEngineState } from '../utils/validation';

const getHasDropdownOptions = (ruleProperty: Property) =>
  [Property.weight, Property.grossMargin, Property.customerOrderCount].includes(ruleProperty);
const getIsMultiSelectDropdown = (ruleProperty: Property) => [Property.weekday].includes(ruleProperty);

export const useRuleEngine = (
  ruleEngineConfig: RuleEngineConfig,
  bindings: RuleEngineBindings,
  initialState: RuleEngineState,
) => {
  const [state, setState] = React.useState<RuleEngineState>(initialState);

  const [propertyAutocompleteValues, setPropertyAutoCompleteValues] = React.useState<
    { property: Property; options: Option[] }[]
  >([]);
  const { shop$ } = useAppContext();
  const [shopCurrency, setShopCurrency] = React.useState<CurrencyCode>(null);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      shop && setShopCurrency(shop.metadata.currency);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [shop$]);

  useEffect(() => {
    bindings.onChange(state, validateRuleEngineState(state));
  }, [state]);

  const toggleMatchAllRuleGroups = (matchAll: boolean) => {
    if (!bindings.viewMode) setState({ ...state, matchAllRuleGroups: matchAll });
  };
  const toggleMatchAllRules = (ruleGroupInternalId: number, matchAll: boolean) => {
    if (!bindings.viewMode)
      setState((prevState) => {
        const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
          if (ruleGroup.internalId === ruleGroupInternalId) {
            return { ...ruleGroup, matchAllRules: matchAll };
          }
          return ruleGroup;
        });
        return { ...prevState, ruleGroups };
      });
  };
  const addBlankRule = (ruleGroupInternalId: number) => {
    const blankRule = {
      property: null,
      operator: null,
      value: undefined,
      apiId: null,
    };
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return {
            ...ruleGroup,
            rules: [
              ...ruleGroup.rules,
              {
                ...blankRule,
                internalId:
                  ruleGroup.rules.map((x) => x.internalId).reduce((prev, current) => Math.max(prev, current), 0) + 1,
              },
            ],
          };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };

  const addBlankRuleGroup = () => {
    const blankRuleGroup = {
      internalId: state.ruleGroups.map((x) => x.internalId).reduce((prev, current) => Math.max(prev, current), 0) + 1,
      matchAllRules: true,
      rules: [],
      apiId: null,
    };
    setState((prevState) => {
      return { ...prevState, ruleGroups: [...prevState.ruleGroups, blankRuleGroup] };
    });
  };

  const removeRuleGroup = (ruleGroupInternalId: number) => {
    setState((prevState) => {
      return {
        ...prevState,
        ruleGroups: prevState.ruleGroups.filter((ruleGroup) => ruleGroup.internalId !== ruleGroupInternalId),
      };
    });
  };
  const removeRuleFromGroup = (ruleGroupInternalId: number, ruleInternalId: number) => {
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return { ...ruleGroup, rules: ruleGroup.rules.filter((rule) => rule.internalId !== ruleInternalId) };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };
  const setRuleProperty = (
    ruleGroupInternalId: number,
    ruleInternalId: number,
    property: Property,
    measuringUnit?: string,
  ) => {
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return {
            ...ruleGroup,
            rules: ruleGroup.rules.map((rule) => {
              if (rule.internalId === ruleInternalId) {
                return {
                  ...rule,
                  property,
                  operator: undefined,
                  value: undefined,
                  measuringUnit: measuringUnit,
                };
              }
              return rule;
            }),
          };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };
  const setRuleOperator = (ruleGroupInternalId: number, ruleInternalId: number, operator: Operator) => {
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return {
            ...ruleGroup,
            rules: ruleGroup.rules.map((rule) => {
              if (rule.internalId === ruleInternalId) {
                return {
                  ...rule,
                  operator,
                };
              }
              return rule;
            }),
          };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };
  const setRuleValue = (ruleGroupInternalId: number, ruleInternalId: number, value: string, optionalValue?: string) => {
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return {
            ...ruleGroup,
            rules: ruleGroup.rules.map((rule) => {
              if (rule.internalId === ruleInternalId) {
                return { ...rule, value, optionalValue };
              }
              return rule;
            }),
          };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };

  const setRuleMeasuringUnit = (ruleGroupInternalId: number, ruleInternalId: number, measuringUnit: string) => {
    setState((prevState) => {
      const ruleGroups = prevState.ruleGroups.map((ruleGroup) => {
        if (ruleGroup.internalId === ruleGroupInternalId) {
          return {
            ...ruleGroup,
            rules: ruleGroup.rules.map((rule) => {
              if (rule.internalId === ruleInternalId) {
                return { ...rule, measuringUnit };
              }
              return rule;
            }),
          };
        }
        return ruleGroup;
      });
      return { ...prevState, ruleGroups };
    });
  };

  const findOperatorsBasedOnProperty = (property: Property): Operator[] => {
    return PROPERTY_OPERATOR_MAPPING.find((mapping) => mapping.property === property)?.operators;
  };

  const renderRuleInputField = (ruleGroupInternalId: number, rule: Rule) => {
    const propertyConfig = ruleEngineConfig.properties.find((x) => x.property == rule.property);
    const type =
      propertyConfig?.conditionalType && rule.operator
        ? propertyConfig.conditionalType(rule.operator)
        : propertyConfig?.type;
    const mapping = PROPERTY_OPERATOR_MAPPING.find(
      (mapping) => mapping.property === rule.property,
    ) as PropertyOperatorMapping;
    const dropdownValueOptions = ValueDropdownOptions[rule.property];
    if (type == RuleEngineConfigPropertyTypes.Number) {
      return (
        <Input
          type={RuleEngineConfigPropertyTypes.Number}
          disabled={bindings.viewMode}
          value={rule.value}
          required={true}
          onChange={(e) => {
            const { name, value } = e.target;
            setRuleValue(ruleGroupInternalId, rule.internalId, value);
          }}
          showValidation={bindings.showValidationErrors}
        />
      );
    } else if (getHasDropdownOptions(rule.property)) {
      const dropdownOptions = [...mapping.dropdownOptions];
      if (rule.property === Property.grossMargin) {
        dropdownOptions[dropdownOptions.length] = {
          value: shopCurrency,
          label: currencyUnitsToCurrencySymbolMap[shopCurrency],
        };
      }

      return (
        <>
          <StepperInput
            onChange={(newValue: number) => {
              setRuleValue(ruleGroupInternalId, rule.internalId, newValue.toString());
            }}
            setValid={(valid: boolean) => {}}
            presetValue={rule.value ? parseFloat(rule.value.toString()) : 0}
            disabled={bindings.viewMode}
          />
          {mapping.dropdownOptions?.length > 0 && (
            <div className="rule-engine-v2__rules-list__item__value__measuring-unit">
              <Dropdown
                options={dropdownOptions}
                onSelect={(option: Option) => {
                  setRuleMeasuringUnit(ruleGroupInternalId, rule.internalId, option.value);
                }}
                disabled={bindings.viewMode}
                preSelectedOption={
                  rule.measuringUnit ? dropdownOptions.find((x) => x.value == rule.measuringUnit) : dropdownOptions[0]
                }
              />
            </div>
          )}
        </>
      );
    } else if (type == RuleEngineConfigPropertyTypes.Autocomplete) {
      return (
        <Autocomplete
          key={rule.property}
          options={propertyAutocompleteValues.find((x) => x.property == rule.property)?.options}
          fetchOptions={async (input: string) => {
            const autocompleteValues = await bindings.fetchAutocompleteValues(rule.property, input);
            setPropertyAutoCompleteValues((prev) => {
              const existingIndex = prev.findIndex((x) => x.property === rule.property);
              const newEntry = {
                property: rule.property,
                options: autocompleteValues
                  ? autocompleteValues.map((x) => ({
                      label: x,
                      value: x,
                    }))
                  : [],
              };
              if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = newEntry;
                return updated;
              }
              return [...prev, newEntry];
            });
          }}
          onChange={(value) => {
            setRuleValue(ruleGroupInternalId, rule.internalId, value);
          }}
          disabled={bindings.viewMode}
          value={rule.value?.toString()}
        />
      );
    } else if (type == 'stepper') {
      return (
        <StepperInput
          onChange={(newValue: number) => {
            setRuleValue(ruleGroupInternalId, rule.internalId, newValue.toString());
          }}
          setValid={(valid: boolean) => {}}
          presetValue={rule.value ? parseFloat(rule.value.toString()) : 0}
          disabled={bindings.viewMode}
        />
      );
    } else if (type == RuleEngineConfigPropertyTypes.ContainsTextOrIsAutocomplete) {
      if (rule.operator == Operator.is || rule.operator == Operator.is_not) {
        return (
          <Autocomplete
            options={propertyAutocompleteValues.find((x) => x.property == rule.property)?.options}
            fetchOptions={async (input: string) => {
              const autocompleteValues = await bindings.fetchAutocompleteValues(rule.property, input);
              setPropertyAutoCompleteValues((prev) => {
                const existingIndex = prev.findIndex((x) => x.property === rule.property);
                const newEntry = {
                  property: rule.property,
                  options: autocompleteValues
                    ? autocompleteValues.map((x) => ({
                        label: x,
                        value: x,
                      }))
                    : [],
                };
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  updated[existingIndex] = newEntry;
                  return updated;
                }
                return [...prev, newEntry];
              });
            }}
            disabled={bindings.viewMode}
            onChange={(value) => {
              setRuleValue(ruleGroupInternalId, rule.internalId, value);
            }}
            placeholder={mapping.placeholder}
            value={rule.value}
          />
        );
      }
      return (
        <Input
          type={'text'}
          disabled={bindings.viewMode}
          value={rule.value}
          required={true}
          onChange={(e) => {
            const { name, value } = e.target;
            setRuleValue(ruleGroupInternalId, rule.internalId, value);
          }}
          showValidation={bindings.showValidationErrors}
        />
      );
    } else if (type == RuleEngineConfigPropertyTypes.Dropdown) {
      const isMultiSelect = getIsMultiSelectDropdown(rule.property);
      const onSelectSingle = (option) => setRuleValue(ruleGroupInternalId, rule.internalId, option.value);
      const onSelectMulti = (options: Option[]) => {
        requestAnimationFrame(() => {
          const values = options.map((option) => option.value);
          setRuleValue(ruleGroupInternalId, rule.internalId, JSON.stringify(values));
        });
      };

      const onSelect = isMultiSelect ? onSelectMulti : onSelectSingle;

      const preSelectedOption =
        !isMultiSelect && rule.value
          ? dropdownValueOptions.find((o) => o.value === rule.value)
          : { label: 'Choose', value: undefined };

      const preSelectedOptions =
        isMultiSelect && rule.value
          ? JSON.parse(rule.value).map((value) => dropdownValueOptions.find((o) => o.value === value))
          : [];

      return (
        <Dropdown
          multiSelect={isMultiSelect}
          options={dropdownValueOptions}
          onSelect={onSelect}
          {...(isMultiSelect
            ? { preSelectedOptions, disabledOptionsOnly: bindings.viewMode }
            : { preSelectedOption, disabled: bindings.viewMode })}
        />
      );
    } else if (type == RuleEngineConfigPropertyTypes.NestedDropdown && rule.property == Property.country) {
      //rule.value is a stringified object like {'US': ['CA', 'NY'], 'CA': ['ON', 'QC'], 'AFR': []} OR plain string like 'United States'
      const onSelectMulti = (statesByCountry: string) => {
        requestAnimationFrame(() => {
          setRuleValue(ruleGroupInternalId, rule.internalId, statesByCountry);
        });
      };
      return (
        <CountriesAndRegionsDropdown
          multiSelect={true}
          disabled={bindings.viewMode}
          preselectedCountriesAndRegionsValue={rule.value}
          onSelect={onSelectMulti}
        />
      );
    }
    return (
      <Input
        type={'text'}
        disabled={!rule.property || !rule.operator || bindings.viewMode}
        value={rule.value}
        required={true}
        onChange={(e) => {
          const { value } = e.target;
          setRuleValue(ruleGroupInternalId, rule.internalId, value);
        }}
        placeholder={mapping?.placeholder ?? ''}
        showValidation={bindings?.showValidationErrors}
      />
    );
  };

  const renderCustomerOrderCountTimerFilters = (ruleGroupInternalId: number, rule: Rule) => {
    return rule.measuringUnit === TimeFrame.WITHIN_LAST_DAYS ? (
      <div className="rule-engine-v2__rules-list__item__value__time-filters">
        <StepperInput
          onChange={(newValue: number) => {
            setRuleValue(ruleGroupInternalId, rule.internalId, rule.value, String(newValue));
          }}
          setValid={(valid: boolean) => {}}
          presetValue={rule.optionalValue ? parseFloat(rule.optionalValue.toString()) : 0}
          disabled={bindings.viewMode}
        />
        <span>Calendar days</span>
      </div>
    ) : null;
  };
  return {
    state,
    toggleMatchAllRuleGroups,
    toggleMatchAllRules,
    addBlankRule,
    addBlankRuleGroup,
    removeRuleGroup,
    removeRuleFromGroup,
    setRuleProperty,
    setRuleOperator,
    setRuleValue,
    setRuleMeasuringUnit,
    findOperatorsBasedOnProperty,
    renderRuleInputField,
    renderCustomerOrderCountTimerFilters,
  };
};
